import CryptoJS from 'crypto-js';
import { IMenuItem } from '@/services/user/types';
import { addPageEtag, getPageEtag } from './localStorage';

/**
 * @Author: Mars
 * @description: 去除路由后面的 /*
 * @param {string} menuUrl
 */
export const getPureMenuUrl = (menuUrl: string) => {
  if (menuUrl.endsWith('/*')) return menuUrl.slice(0, -2);
  return menuUrl;
};

/**
 * @Author: Mars
 * @description: 单位转换
 * @param {number} value 值
 * @param {string} unit 单位
 * @param {number} decimal 精度
 */
export const unitConversion = (value = 0, unit?: string, decimal?: number) => {
  let finalUnit = unit;
  let finalValue = value;
  if (!unit) {
    return { finalUnit: unit, finalValue: value };
  }
  const tmp = Number(value / 1000);
  if (Math.abs(tmp) > 1000) {
    if (unit === 'kWh') finalUnit = 'GWh';
    if (unit === 'kW') finalUnit = 'GW';
    if (unit === 'kg') finalUnit = 'kt';
    finalValue = Number(tmp / 1000);
  } else if (Math.abs(tmp) > 1) {
    if (unit === 'kWh') finalUnit = 'MWh';
    if (unit === 'kW') finalUnit = 'MW';
    if (unit === 'kg') finalUnit = 't';
    finalValue = tmp;
  } else {
    finalUnit = unit;
    finalValue = value;
  }
  if (decimal) finalValue = Number(formatValueDecimal(finalValue, decimal));
  finalValue = formatDecimalNumber(finalValue);
  return { finalUnit, finalValue };
};

/**
 * @Author: Wayne Hu
 * @description: 单位换算2
 * @param {*} value
 * @param {string} unit
 * @param {number} decimal
 */
export const unitConversion2 = (value = 0, unit?: string, decimal?: number) => {
  let finalUnit2 = unit;
  let finalValue2 = value;
  if (!unit) {
    return { finalUnit2: unit, finalValue2: value };
  }
  const tmp = Number(value / 1000);
  if (Math.abs(tmp) > 1000) {
    if (unit === 'kWh') finalUnit2 = 'GWh';
    if (unit === 'kW') finalUnit2 = 'GW';
    if (unit === 'kg') finalUnit2 = 'kt';
    finalValue2 = Number(tmp / 1000);
  } else if (Math.abs(tmp) > 1) {
    if (unit === 'kWh') finalUnit2 = 'MWh';
    if (unit === 'kW') finalUnit2 = 'MW';
    if (unit === 'kg') finalUnit2 = 't';
    finalValue2 = tmp;
  } else {
    finalUnit2 = unit;
    finalValue2 = value;
  }
  if (decimal) finalValue2 = Number(formatValueDecimal(finalValue2, decimal));
  finalValue2 = formatDecimalNumber(finalValue2);
  return { finalUnit2, finalValue2 };
};

/**
 * @description: 处理数值精度
 * @param {number} num
 * @return {*}
 * @LastEditors: April
 */
export const formatDecimalNumber = (num: number) => {
  num = num ?? 0;
  if (Number.isInteger(num) || (Math.round(num * 100) / 100).toFixed(2).endsWith('00')) {
    return Number(num.toFixed(0));
  } else {
    return Number((Math.round(num * 100) / 100).toFixed(2));
  }
};
/**
 * @Author: Mars
 * @description:
 * @param {*} value 数值
 * @param {*} digit 精度
 * @return {*}
 */
export const formatValueDecimal = (value = 0, decimal = 2) => {
  // 如果本身不包含小数点，不进行处理
  if (Number.isInteger(value)) {
    return value.toString();
  }

  return value.toFixed(decimal);
};
/**
 * @Author: Mars
 * @description: 复制内容到剪切板
 * @param {string} textToCopy
 * @return {*}
 */
export const copyToClipboard = async (textToCopy: string) => {
  if (window.navigator.clipboard && window.isSecureContext) {
    await window.navigator.clipboard.writeText(textToCopy);
  } else {
    const textArea = document.createElement('textarea');
    textArea.value = textToCopy;

    textArea.style.position = 'absolute';
    textArea.style.left = '-999999px';

    document.body.prepend(textArea);
    textArea.select();

    try {
      document.execCommand('copy');
    } catch (error) {
      console.error(error);
    } finally {
      textArea.remove();
    }
  }
};

/**
 * @Author: Mars
 * @description: 判断是否需要刷新页面
 * @param {string} url
 * @return {*}
 */
export const needRefresh = async (url: string): Promise<boolean> => {
  const etag = getPageEtag(url);
  const resp = await fetch(`${url}`, { headers: { 'If-None-Match': etag ?? '' } });
  if (resp.ok) {
    if (resp.status === 200) {
      const respEtag = resp.headers.get('Etag') || '';
      addPageEtag(url, respEtag);
      // 如果原本有etag，则代表需要刷新
      return Boolean(etag);
    } else {
      return false;
    }
  } else {
    return false;
  }
};

/**
 * @Author: April
 * @description: 哈希512加密
 * @param {string} content
 * @return {*}
 */
export const hashStringWithSHA512 = (content: string) => {
  const sha1Hash = CryptoJS.SHA512(content);
  return sha1Hash.toString(CryptoJS.enc.Hex);
};

/**
 * @Author: Wayne Hu
 * @description: 日期时间格式化
 * @param {string} dateString
 */
export const formatDate = (dateString: string) => {
  const timeStr: string = dateString.split('.')[0].replace('T', ' ');
  return timeStr;
};

/**
 * @Author: Wayne Hu
 * @description: 查找满足条件的菜单
 * @param {IMenuItem} menus
 * @param {string} targetUrls
 */
export const findValidMenus = (menus: IMenuItem[], targetUrls: string[]): IMenuItem[] => {
  const result: IMenuItem[] = [];
  for (const menu of menus) {
    if (targetUrls.includes(menu.menuUrl)) {
      result.push(menu);
    } else if (menu.children) {
      const validChildren = findValidMenus(menu.children, targetUrls);
      if (validChildren.length > 0) {
        result.push({ ...menu, children: validChildren });
      }
    }
  }
  return result;
};
/**
 * @Author: Wayne Hu
 * @description: 判断某个菜单是否存在
 * @param {IMenuItem} menus
 * @param {string} targetUrl
 */
export const findMenuItem = (menus: IMenuItem[], targetUrl: string): boolean => {
  for (const item of menus) {
    if (item.menuUrl === targetUrl) {
      return true;
    }
    if (item.children && item.children.length > 0 && findMenuItem(item.children, targetUrl)) {
      return true;
    }
  }
  return false;
};

/**
 * @Author: YuKi
 * @description: 根据key获取url上的固定参数链接
 * @param {string} url
 * @return {*}
 */
export const getQueryParams = (key: string) => {
  const queryString = window.location.search;
  const params = new URLSearchParams(queryString);
  if (params) {
    return params.get(key) || undefined;
  }
  return undefined;
};
